import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiService } from "../services/api";
import { Database, Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [seedingDemo, setSeedingDemo] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      toast.success("Login realizado com sucesso!");
      navigate("/dashboards");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao fazer login");
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDemo = async () => {
    setSeedingDemo(true);
    try {
      const response = await apiService.seedDemo();
      toast.success("Dados de demonstração criados!");
      setFormData({
        email: response.data.user.email,
        password: response.data.user.password,
      });
    } catch (error) {
      toast.error("Erro ao criar dados de demonstração");
    } finally {
      setSeedingDemo(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-2/5 bg-gradient-to-br from-indigo-600 to-indigo-800 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1763303775599-08d20753a9ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHdoaXRlJTIwM0QlMjBnZW9tZXRyaWMlMjBzaGFwZXMlMjBtaW5pbWFsaXN0JTIwbGlnaHQlMjBicmlnaHR8ZW58MHx8fHwxNzcyNjc0NDU4fDA&ixlib=rb-4.1.0&q=85')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative z-10 flex flex-col justify-center px-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
              <Database className="w-7 h-7 text-white" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-white">
              UP2 Firebird BI
            </h1>
          </div>
          <h2 className="font-heading text-4xl font-semibold text-white leading-tight mb-4">
            Transforme seus dados em insights
          </h2>
          <p className="text-indigo-100 text-lg max-w-md">
            Crie dashboards dinâmicos e extrações de dados de forma visual, sem
            escrever SQL. Use o WILL, nossa IA, para criar análises
            automaticamente.
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="flex lg:hidden items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Database className="w-6 h-6 text-white" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">
              UP2 Firebird BI
            </h1>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl font-semibold text-slate-900">
                Bem-vindo de volta
              </h2>
              <p className="text-slate-500 mt-2">
                Entre com suas credenciais para acessar
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    required
                    className="pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Entrando...
                  </>
                ) : (
                  "Entrar"
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-200">
              <p className="text-center text-sm text-slate-500 mb-4">
                Não tem uma conta? Crie dados de demonstração:
              </p>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleSeedDemo}
                disabled={seedingDemo}
                data-testid="seed-demo-btn"
              >
                {seedingDemo ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Criando...
                  </>
                ) : (
                  "Criar Dados de Demonstração"
                )}
              </Button>
            </div>
          </div>

          <p className="text-center text-xs text-slate-400 mt-6">
            UP2 Firebird BI © 2026
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
